/**
 * Timezone helpers shared by every admin screen that shows or sends instants.
 *
 * The backend stores and returns UTC instants; the product's "day" is the
 * LOCATION's calendar day, so the admin has to translate in both directions:
 * instants -> location wall-clock time (display) and location calendar day ->
 * UTC instants (analytics range queries).
 */

/** The viewer's own IANA timezone — the fallback whenever a location's is unknown. Never 'UTC' by accident. */
export function browserTimeZone(): string {
  try {
    return new Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** True when `timeZone` is an IANA id this runtime's Intl can resolve. */
export function isValidTimeZone(timeZone: string | null | undefined): timeZone is string {
  if (!timeZone) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}

/** `timeZone` when valid, else the browser's. */
export function resolveTimeZone(timeZone: string | null | undefined): string {
  return isValidTimeZone(timeZone) ? timeZone : browserTimeZone();
}

/** Offset (ms) of `timeZone` from UTC at the given instant: local wall-clock minus UTC. */
function offsetMs(instantMs: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  }).formatToParts(new Date(instantMs));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  );
  // Drop sub-second precision on the instant so the difference is whole seconds.
  return asUtc - Math.floor(instantMs / 1000) * 1000;
}

/** The UTC instant of the FIRST moment of local calendar day `y-m-d` in `timeZone`. */
function startOfLocalDayMs(y: number, m: number, d: number, timeZone: string): number {
  const target = `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const guess = Date.UTC(y, m - 1, d, 0, 0, 0);
  const first = guess - offsetMs(guess, timeZone);
  // The offset can differ at the corrected instant when a DST change falls
  // between the guess and the real midnight; re-apply once with that offset.
  let ms = guess - offsetMs(first, timeZone);
  const STEP = 15 * 60 * 1000;
  const key = (t: number) => localDateKey(new Date(t), timeZone);
  // Midnight may not exist (spring-forward at 00:00, e.g. Santiago, Havana):
  // the arithmetic then lands before the day starts — advance to the first
  // instant whose local date is the target day.
  while (key(ms) < target) ms += STEP;
  // Midnight may occur twice (fall-back at 00:00): take the earlier one.
  while (key(ms - STEP) === target) ms -= STEP;
  return ms;
}

/**
 * UTC instants bounding the local calendar days `fromDay`..`toDay`
 * (inclusive, 'YYYY-MM-DD') in `timeZone`: `from` is local 00:00:00.000 of
 * `fromDay`, `to` is local 23:59:59.999 of `toDay`. DST-safe (days can be 23
 * or 25 hours long).
 *
 * Sending these to the analytics endpoints makes "the selected days" mean the
 * location's days end to end — the backend buckets and zero-fills by the
 * location's local date, so a bare `T00:00:00.000Z` midnight would start the
 * axis a day early in any timezone west of UTC.
 */
export function localDayRangeToUtc(
  fromDay: string,
  toDay: string,
  timeZone: string,
): { from: string; to: string } {
  const tz = resolveTimeZone(timeZone);
  const [fy, fm, fd] = fromDay.split('-').map(Number) as [number, number, number];
  const [ty, tm, td] = toDay.split('-').map(Number) as [number, number, number];
  const fromMs = startOfLocalDayMs(fy, fm, fd, tz);
  // Start of the NEXT local day minus 1 ms (Date.UTC rolls month/year over).
  const nextDay = new Date(Date.UTC(ty, tm - 1, td + 1));
  const toMs =
    startOfLocalDayMs(
      nextDay.getUTCFullYear(),
      nextDay.getUTCMonth() + 1,
      nextDay.getUTCDate(),
      tz,
    ) - 1;
  return { from: new Date(fromMs).toISOString(), to: new Date(toMs).toISOString() };
}

/** Today's calendar day ('YYYY-MM-DD') as seen in `timeZone`. */
export function localDateKey(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: resolveTimeZone(timeZone) }).format(date);
}
